import { cloneDeep, sortBy, unionBy } from "lodash";
import dayjs from "dayjs";

import {
  APPLICATION_TAB_NAMES,
  JOB_OPPORTUNITY_STATUSES,
  MATCH_TAB_STATUSES,
  PRE_CANDIDATES_TAB_STATUSES,
  ROWS_PER_PAGE_ON_PAGINATED_TABLE,
  SHORTLISTED_TAB_STATUSES,
  SKIPPED_TAB_STATUSES,
} from "lookup";
import api from "apiSingleton";
import { initialState } from "context/reducers/jobs";

const ITEM_LIMIT = 500;

function sortByStatus(jobs) {
  const jobStatusesOrder = [
    JOB_OPPORTUNITY_STATUSES.ACTIVE,
    JOB_OPPORTUNITY_STATUSES.PENDINGAPPROVAL,
    JOB_OPPORTUNITY_STATUSES.FULFILLED,
    JOB_OPPORTUNITY_STATUSES.RDFULFILLED,
    JOB_OPPORTUNITY_STATUSES.CANCELLED,
    JOB_OPPORTUNITY_STATUSES.DRAFT,
  ];

  return sortBy(jobs, [
    function (job) {
      return jobStatusesOrder.indexOf(job.status);
    },
    jobs.sort((a, b) =>
      dayjs(a.createdAt).isAfter(dayjs(b.createdAt)) ? -1 : 1
    ),
  ]);
}

const mapMatches = (matches) => {
  if (matches?.items) {
    return matches.items.map((el) => ({
      ...el,
      isMatching: false,
      isShortlisting: false,
      isDeleting: false,
    }));
  }

  return [];
};

const loadJobOpportunities = async (nextToken = null, filters) => {
  if (filters.length === 0) {
    return [];
  }

  const response = await api.jobs.list({
    filter: {
      status: {
        in: filters,
      },
    },
    limit: ITEM_LIMIT,
    nextToken,
  });

  const jobs = response.data.listJobOpportunitys;

  if (jobs.nextToken) {
    await new Promise((resolve) => setTimeout(resolve, 250));
    const jobsNextPortions = await loadJobOpportunities(
      jobs.nextToken,
      filters
    );

    return [...jobs.items, ...jobsNextPortions];
  }

  return jobs.items;
};

export async function initializeJobs(filters = initialState.statusFilter) {
  const jobs = await loadJobOpportunities(null, filters);

  const jobsByStatus = sortByStatus(jobs);
  const companyNames = {};
  const jobTypes = {};
  const jobSkills = {};
  const jobOptionalSkills = {};
  const torcOwners = {};

  const jobOpps = [
    ...jobsByStatus?.map((el) => ({
      ...el,
      matches: { items: mapMatches(el.matches) },
    })),
  ];

  for (let job of jobOpps) {
    const organizationName = job?.organization;
    const jobType = job?.jobType?.title;

    const skills = job?.TKSkills || [];

    const optionalSkills = job?.optionalTKSkills || [];

    if (organizationName && !companyNames[organizationName]) {
      companyNames[organizationName] = organizationName;
    }

    if (job?.torcOwner && !torcOwners[job.torcOwner.username]) {
      torcOwners[job.torcOwner.username] = job.torcOwner;
    }

    if (jobType && !jobTypes[jobType]) {
      jobTypes[jobType] = true;
    }

    if (skills.length > 0) {
      skills.forEach((skill) => {
        if (!jobSkills[`${skill.name}_${skill.category}`]) {
          jobSkills[`${skill.name}_${skill.category}`] = skill;
        }
      });
    }

    if (optionalSkills.length > 0) {
      optionalSkills.forEach((skill) => {
        if (!jobOptionalSkills[`${skill.name}_${skill.category}`]) {
          jobOptionalSkills[`${skill.name}_${skill.category}`] = skill;
        }
      });
    }
  }

  return {
    jobOpps,
    companyNames,
    jobTypes,
    jobSkills,
    torcOwners,
    jobOptionalSkills,
  };
}

const removeErrorProperty = (data, path, message) => {
  if (!data || path.length === 0) return data;

  const [current, ...restPath] = path;

  if (data[current] && (current === "user" || data[current]?.applicationId)) {
    data[current].apiErrors = [
      ...(data?.[current]?.apiErrors || []),
      {
        path,
        message,
      },
    ];
  }

  if (!(current in data)) return data;

  if (restPath.length === 1) {
    delete data[current];

    if (Array.isArray(data)) {
      data = data.filter((item) => item);
    }
  } else {
    data[current] = removeErrorProperty(data[current], restPath, message);
  }

  return data;
};

const getMatchesByStatus = async (status, params, addGraphQLAlert) => {
  try {
    const getNextMatches = await api.jobs.getNextMatches({
      ...params,
    });

    return {
      matches: getNextMatches?.data?.getJobOpportunity?.matches?.items || [],
      nextToken: getNextMatches?.data?.getJobOpportunity?.matches?.nextToken,
      status,
    };
  } catch (err) {
    addGraphQLAlert(err, undefined, 35000);

    if (
      !err.errors ||
      !err.errors.some(
        ({ path }) =>
          path &&
          (path.includes("user") ||
            path.includes("matches") ||
            path.includes("getJobOpportunity"))
      )
    ) {
      throw err;
    }

    const errorResponse = err?.data?.getJobOpportunity ?? {};

    err.errors.forEach(({ path, message }) => {
      if (path) {
        path.shift();
        removeErrorProperty(errorResponse, path, message);
      }
    });

    return {
      matches: errorResponse?.matches?.items || [],
      nextToken: err?.data?.getJobOpportunity?.matches?.nextToken,
      hasErrors: true,
      status,
    };
  }
};

const getUserJobInterest = async (params, addGraphQLAlert) => {
  try {
    const userInterests = await api.jobLongList.listJobLongList({
      ...params,
    });

    const result = userInterests?.data?.listJobLongList;

    return {
      items: result?.items || [],
      nextToken: result?.nextToken,
    };
  } catch (err) {
    addGraphQLAlert(err, undefined, 35000);

    if (
      !err.errors ||
      !err.errors.some(({ path }) => path && path.includes("user"))
    ) {
      return {
        items: [],
        nextToken: null,
        hasErrors: true,
      };
    }

    const errorResponse = err?.data?.listJobLongList ?? {};

    err.errors.forEach(({ path, message }) => {
      if (path) {
        path.shift();
        removeErrorProperty(errorResponse, path, message);
      }
    });

    return {
      items: errorResponse?.items || [],
      nextToken: errorResponse?.nextToken,
      hasErrors: true,
    };
  }
};

const formatTabResults = (tabResults) => {
  const matches = tabResults
    .map((e) => {
      if (e.value && e.value.matches) {
        return e.value.matches;
      }

      return null;
    })
    .filter((e) => e)
    .flat();

  const nextTokensByStatus = tabResults
    .map((e) => {
      if (e.value) {
        return { status: e.value.status, nextToken: e.value.nextToken };
      }

      return null;
    })
    .filter((e) => e);

  return {
    matches,
    nextTokensByStatus,
    hasErrors: tabResults.some((e) => e.status === "rejected"),
  };
};

export const getTabMatches = async (
  id,
  statuses,
  matchLimit,
  addGraphQLAlert
) => {
  const matchedTabPromises = statuses.map(({ status, nextToken }) =>
    getMatchesByStatus(
      status,
      {
        id,
        nextToken,
        matchLimit,
        matchFilter: { status: { eq: status } },
      },
      addGraphQLAlert
    )
  );

  const matchTabResults = await Promise.allSettled(matchedTabPromises);

  return formatTabResults(matchTabResults);
};

export const getTabMatchesByNumberToFetch = async (
  jobId,
  statusesReq,
  addGraphQLAlert,
  itemsPerPage = ROWS_PER_PAGE_ON_PAGINATED_TABLE,
  limit = 100
) => {
  let nexTokenStatusReq = cloneDeep(statusesReq);

  const matches = [];

  let nextTokensByStatus = [];

  let hasErrors = false;

  do {
    const response = await getTabMatches(
      jobId,
      nexTokenStatusReq,
      limit,
      addGraphQLAlert
    );

    matches.push(...response.matches);

    nextTokensByStatus = unionBy(
      response.nextTokensByStatus,
      nextTokensByStatus,
      "status"
    );

    if (!hasErrors) {
      hasErrors = response.hasErrors;
    }

    nexTokenStatusReq =
      response?.nextTokensByStatus?.filter((e) => e.nextToken) || [];
  } while (
    (itemsPerPage &&
      matches.length < itemsPerPage &&
      nexTokenStatusReq.length > 0) ||
    (itemsPerPage === null && nexTokenStatusReq.length > 0)
  );

  return { matches, nextTokensByStatus, hasErrors };
};

export const getJobOpportunityUserInterests = async (
  jobOpportunityId,
  addGraphQLAlert,
  limit = 100
) => {
  let nextToken = null;
  const items = [];
  let hasErrors = false;

  do {
    const response = await getUserJobInterest(
      {
        jobOpportunityId,
        filter: {
          not: {
            convertedToMatch: { eq: true },
          },
        },
        limit,
        nextToken,
      },
      addGraphQLAlert
    );

    items.push(...response.items);

    if (!hasErrors) {
      hasErrors = response.hasErrors;
    }

    nextToken = response.nextToken;
  } while (nextToken);

  const normalizedItems = items.map((item) => ({
    ...item,
    canConvertToMatch: true,
  }));

  return {
    items: normalizedItems,
    nextToken,
    hasErrors,
  };
};

export async function updateJobLongList(args) {
  try {
    const resp = await api.jobLongList.updateJobLongList({
      input: {
        ...args,
      },
    });

    return resp.data?.updateJobLongList || {};
  } catch (error) {
    console.warn(`updateJobLongList error: ${error}`);
  }
}

export async function initializeJob(id, { addGraphQLAlert, addCustomAlert }) {
  let jobDetails;

  let hasErrors = false;

  try {
    const job = await api.jobs.get({ id });
    jobDetails = job.data.getJobOpportunity;
  } catch (err) {
    if (
      !err.errors ||
      !err.errors.some(
        ({ path }) =>
          path &&
          (path.includes("user") ||
            path.includes("matches") ||
            path.includes("getJobOpportunity"))
      )
    ) {
      throw err;
    }

    hasErrors = true;

    jobDetails = err?.data?.getJobOpportunity ?? {};

    err.errors.forEach(({ path, message }) => {
      if (path) {
        path.shift();
        removeErrorProperty(jobDetails, path, message);
      }
    });

    addGraphQLAlert(err, undefined, 35000);
  }

  const matchTabResult = await getTabMatchesByNumberToFetch(
    id,
    MATCH_TAB_STATUSES,
    addGraphQLAlert
  );

  const shortlistedTabResult = await getTabMatchesByNumberToFetch(
    id,
    SHORTLISTED_TAB_STATUSES,
    addGraphQLAlert
  );

  const skippedTabResult = await getTabMatchesByNumberToFetch(
    id,
    SKIPPED_TAB_STATUSES,
    addGraphQLAlert,
    350
  );

  const preShortlistedResults = await getTabMatchesByNumberToFetch(
    id,
    PRE_CANDIDATES_TAB_STATUSES,
    addGraphQLAlert,
    null
  );

  jobDetails.matches.items = [
    ...matchTabResult.matches,
    ...shortlistedTabResult.matches,
    ...skippedTabResult.matches,
    ...preShortlistedResults.matches,
  ];

  jobDetails.matchTabs = {
    [APPLICATION_TAB_NAMES.MATCHED]: {
      nextTokensByStatus: matchTabResult.nextTokensByStatus,
    },
    [APPLICATION_TAB_NAMES.SHORTLISTED]: {
      nextTokensByStatus: shortlistedTabResult.nextTokensByStatus,
    },
    [APPLICATION_TAB_NAMES.SKIPPED]: {
      nextTokensByStatus: skippedTabResult.nextTokensByStatus,
    },
  };

  if (
    hasErrors ||
    matchTabResult.hasErrors ||
    shortlistedTabResult.hasErrors ||
    skippedTabResult.hasErrors ||
    preShortlistedResults.hasErrors
  ) {
    addCustomAlert({
      title: "Error contact support",
      duration: 20000,
      message:
        "There was an issue with the fetched data please contact administrator for support",
    });
  }

  jobDetails.matches?.items.forEach((item) => {
    if (item?.user?.id) {
      const user = item.user;
      // Similar logic in place in algolia processor - so any updates here must reflect there
      // and vice versa
      if (user.placements?.length > 0) {
        for (const placement of user.placements) {
          const placementEndDate = placement.actualEndDate ?? placement.endDate;
          placement.endDateTimeStamp = Math.floor(
            new Date(placementEndDate).getTime() / 1000
          );
        }

        const nowTimeStamp = Math.floor(new Date().getTime() / 1000);

        user.hasActivePlacements = user.placements.some(
          (placement) => nowTimeStamp < placement.endDateTimeStamp
        );

        user.hasPlacements = true;
      }

      const resumeParagraphs = user.documentsText?.items?.find(
        (e) => e.type === "RESUME"
      )?.paragraphs;

      user.parsedResume = JSON.parse(resumeParagraphs || "[]");
    }
  });

  return jobDetails;
}

export async function initializeJobCalendarEvents(id) {
  try {
    const { data } = await api.jobs.getEvents({ id });
    return data.getJobOpportunityEvents;
  } catch (error) {
    console.log("Something went wrong when fetching job calendar events");
    return [];
  }
}

export function updateJobLocally(job, value, valueKey) {
  const clonedJob = cloneDeep(job);
  const updatedJob = { ...clonedJob, [valueKey]: value };

  return updatedJob;
}

export async function updateJob(id, payload) {
  const job = await api.jobs.update({
    input: {
      id,
      ...payload,
    },
  });

  return job;
}

export async function reRankHitsForJobs(jobId, hits, addCustomAlert) {
  try {
    return await api.jobs.reRankHits({ jobId, hits });
  } catch (err) {
    addCustomAlert({
      title: "Match agent error",
      duration: 5000,
      message: err.message || "Error Ranking hits",
    });

    return;
  }
}
